<template>
  <div class="flex flex-col">
    <InputMask
      ref="inputMask"
      v-model="localDate"
      v-bind="{
        id,
        disabled,
        placeholder,
        invalid,
        readonly
      }"
      mask="99.99.9999 - 99.99.9999"
      slotChar="дд.мм.гггг - дд.мм.гггг"
      :autoClear="false"
      :class="inputClass"
      :pt="pt"
      @blur="onBlur"
      @focus="onFocus"
      @keydown.tab="onTab"
    />

    <Calendar
      ref="calendar"
      v-model="calendarModel"
      selectionMode="range"
      inputClass="hidden"
      :maxDate="props.disableFuture ? today : undefined"
    />
  </div>
</template>

<script setup lang="ts">
import { defineModel, nextTick, ref, watch } from 'vue';
import InputMask from 'primevue/inputmask';
import { InputEmits, InputProps, dateBackendFormat, dateFrontFormat } from '@/shared';
import Calendar from 'primevue/calendar';

const props = defineProps<
  InputProps & {
    inputClass?: string;
    pt?: Record<string, any>;
    disableFuture?: boolean;
  }
>();
const emits = defineEmits<InputEmits>();

const today = new Date();

const calendar = ref();
const date1 = defineModel<string | null>('date1');
const date2 = defineModel<string | null>('date2');
const localDate = ref<string | null>();
const calendarModel = ref<Date[]>();

setLocalDateValue();

const regExp = /[а-яА-ЯЁё]/;
const regExpLetters = /[а-яА-ЯёЁa-zA-Z]/g;
const fullDatePattern = /^\d{2}\.\d{2}\.\d{4}$/;

// При изменении внутреннего значения изменяем внешнее
watch(localDate, date => {
  if (date) {
    const [_date1, _date2] = date.split('-').map(date => date.trim());

    if (!regExp.test(_date1)) {
      date1.value = dateBackendFormat(_date1);
    } else {
      date1.value = null;
    }

    if (!regExp.test(_date2)) {
      date2.value = dateBackendFormat(_date2);
    } else {
      date2.value = null;
    }

    const date1_ = _date1.replace(regExpLetters, '').trim();
    const date2_ = _date2.replace(regExpLetters, '').trim();

    if (fullDatePattern.test(date1_) && isValidDate(date1_)) {
      emits('change', { date1, date2 });
    } else if (fullDatePattern.test(date1_)) {
      resetDateInInput(1);

      return;
    }

    if (fullDatePattern.test(date2_) && isValidDate(date2_)) {
      emits('change', { date1, date2 });
    } else if (fullDatePattern.test(date2_)) {
      resetDateInInput(2);

      return;
    }

    emits('change', { date1, date2 });
  }
});

function resetDateInInput(part: 1 | 2) {
  const parts = localDate.value?.split('-') ?? [];
  if (!parts.length) return;

  if (part === 1) {
    parts[0] = 'дд.мм.гггг ';
  } else if (part === 2) {
    parts[1] = ' дд.мм.гггг';
  }

  localDate.value = parts.join('-');
}

function isValidDate(dateStr: string): boolean {
  const [day, month, year] = dateStr.split('.').map(Number);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1000 || year > 9999) return false;

  const d = new Date(year, month - 1, day);
  const isReal = d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;

  if (!isReal) return false;

  if (props.disableFuture) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d > today) return false;
  }

  return true;
}

watch(calendarModel, calendarModel => {
  if (calendarModel) {
    const [_date1, _date2] = calendarModel;
    date1.value = _date1?.toLocaleDateString();
    date2.value = _date2?.toLocaleDateString();
    nextTick(() => {
      setLocalDateValue();
    });
  }
});

function setLocalDateValue() {
  const _date1 = dateFrontFormat(date1.value || '');
  const _date2 = dateFrontFormat(date2.value || '');

  localDate.value = `${_date1} - ${_date2}`;
}

function onFocus(event: Event) {
  calendar.value.onFocus(event);
  emits('focus', event);
}

function onBlur(event: Event) {
  emits('blur', event);
}

function onTab() {
  calendar.value.overlayVisible = false;
}
</script>
